import type {
  OrdinalOutputRecord,
  SatAttributeCount,
  SatRarity,
  SpecialSatProfile,
  WalletSatSource,
} from "./types";

const HALVING_INTERVAL = 210_000;
const DIFFICULTY_INTERVAL = 2_016;
const CYCLE_INTERVAL = HALVING_INTERVAL * 6;
const MAX_SUBSIDY_BLOCKS = 6_930_000;

export function firstSatOfBlock(height: number): bigint {
  if (!Number.isInteger(height) || height < 0 || height > MAX_SUBSIDY_BLOCKS) {
    throw new Error("Invalid Bitcoin block height");
  }
  let remaining = BigInt(height);
  let subsidy = 5_000_000_000n;
  let firstSat = 0n;
  while (remaining > 0n && subsidy > 0n) {
    const blocks = remaining > BigInt(HALVING_INTERVAL) ? BigInt(HALVING_INTERVAL) : remaining;
    firstSat += blocks * subsidy;
    remaining -= blocks;
    subsidy >>= 1n;
  }
  return firstSat;
}

export function blockContainingSat(sat: bigint): number {
  if (sat < 0n) throw new Error("Invalid sat number");
  let low = 0;
  let high = MAX_SUBSIDY_BLOCKS;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (firstSatOfBlock(middle) <= sat) low = middle;
    else high = middle - 1;
  }
  return low;
}

export function satRarity(sat: number): SatRarity {
  if (!Number.isSafeInteger(sat) || sat < 0) throw new Error("Invalid sat number");
  const height = blockContainingSat(BigInt(sat));
  if (firstSatOfBlock(height) !== BigInt(sat)) return "common";
  if (height === 0) return "mythic";
  if (height % CYCLE_INTERVAL === 0) return "legendary";
  if (height % HALVING_INTERVAL === 0) return "epic";
  if (height % DIFFICULTY_INTERVAL === 0) return "rare";
  return "uncommon";
}

interface LocatedRodarmorSat {
  sat: number;
  offset: number;
  rarity: Exclude<SatRarity, "common">;
}

export interface LocatedNotableSat {
  sat: number;
  offset: number;
  rarity: SatRarity;
  primarySlug: string;
  primaryLabel: string;
  attributes: SatAttributeCount[];
}

export const SAT_SUPPLY = 2_099_999_997_690_000;

export function satName(sat: number): string {
  if (!Number.isSafeInteger(sat) || sat < 0 || sat >= SAT_SUPPLY) throw new Error("Invalid sat number");
  let value = BigInt(SAT_SUPPLY) - BigInt(sat);
  let name = "";
  while (value > 0n) {
    name = "abcdefghijklmnopqrstuvwxyz"[Number((value - 1n) % 26n)] + name;
    value = (value - 1n) / 26n;
  }
  return name;
}

const rarityPriority: Record<SatRarity, number> = {
  mythic: 0,
  legendary: 1,
  epic: 2,
  rare: 3,
  uncommon: 4,
  common: 5,
};

function titleCase(value: string): string {
  return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;
}

export function satOffsetInRanges(ranges: Array<[number, number]>, sat: number): number | null {
  if (!Number.isSafeInteger(sat) || sat < 0) return null;
  let offset = 0;
  for (const range of ranges) {
    const [start, end] = range;
    if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 0 || end <= start) return null;
    if (sat >= start && sat < end) return offset + sat - start;
    offset += end - start;
  }
  return null;
}

export function satAtOffsetInRanges(ranges: Array<[number, number]>, targetOffset: number): number | null {
  if (!Number.isSafeInteger(targetOffset) || targetOffset < 0) return null;
  let offset = targetOffset;
  for (const range of ranges) {
    const [start, end] = range;
    if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 0 || end <= start) return null;
    const length = end - start;
    if (offset < length) return start + offset;
    offset -= length;
  }
  return null;
}

export function rodarmorSatsInRanges(ranges: Array<[number, number]>): LocatedRodarmorSat[] {
  const located: LocatedRodarmorSat[] = [];
  let rangeOffset = 0;
  for (const [start, end] of ranges) {
    if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 0 || end <= start) continue;
    let height = blockContainingSat(BigInt(start));
    if (firstSatOfBlock(height) < BigInt(start)) height += 1;
    while (height <= MAX_SUBSIDY_BLOCKS) {
      const candidate = firstSatOfBlock(height);
      if (candidate >= BigInt(end)) break;
      const sat = Number(candidate);
      const rarity = satRarity(sat);
      if (rarity !== "common") located.push({ sat, offset: rangeOffset + sat - start, rarity });
      height += 1;
    }
    rangeOffset += end - start;
  }
  return located;
}

export function notableSatsInOutput(
  output: OrdinalOutputRecord,
  profiles: SpecialSatProfile[] = [],
): LocatedNotableSat[] {
  const bySat = new Map<number, LocatedNotableSat>();
  for (const located of rodarmorSatsInRanges(output.sat_ranges)) {
    bySat.set(located.sat, {
      ...located,
      primarySlug: located.rarity,
      primaryLabel: titleCase(located.rarity),
      attributes: [{ slug: located.rarity, label: titleCase(located.rarity), count: 1 }],
    });
  }
  for (const profile of profiles) {
    const offset = satOffsetInRanges(output.sat_ranges, profile.sat);
    if (offset === null) continue;
    const existing = bySat.get(profile.sat);
    const attributes = new Map((existing?.attributes ?? []).map((attribute) => [attribute.slug, attribute]));
    for (const attribute of profile.attributes) {
      if (!attribute.slug || !attribute.label || !Number.isSafeInteger(attribute.count) || attribute.count <= 0) continue;
      attributes.set(attribute.slug, attribute);
    }
    bySat.set(profile.sat, {
      sat: profile.sat,
      offset,
      rarity: existing?.rarity ?? satRarity(profile.sat),
      primarySlug: profile.primarySlug || existing?.primarySlug || satRarity(profile.sat),
      primaryLabel: profile.primaryLabel || existing?.primaryLabel || titleCase(satRarity(profile.sat)),
      attributes: [...attributes.values()],
    });
  }
  return [...bySat.values()].sort((left, right) => left.offset - right.offset || left.sat - right.sat);
}

/** Expand only independently known category ranges, never an indexer's count. */
export function notableSatsInBundle(output: OrdinalOutputRecord, profiles: SpecialSatProfile[] = []): LocatedNotableSat[] {
  const exactProfiles = profiles.filter((profile) => profile.outpoint.toLowerCase() === output.outpoint.toLowerCase());
  const located = notableSatsInOutput(output, exactProfiles);
  const categories = [
    { start: 45_000_000_000, end: 45_100_000_000, attributes: [
      { slug: "b9-450", label: "1st Bitcoin Sats (B9 450x)", count: 1 },
      { slug: "b9", label: "Block 9", count: 1 },
      { slug: "1st-tx", label: "1st Tx", count: 1 },
      { slug: "nakamoto", label: "Nakamoto", count: 1 },
      { slug: "vintage", label: "Vintage", count: 1 },
    ] },
    // Subsidy of Parasite Pool's first mined block, height 938,713.
    { start: 1_999_597_812_500_000, end: 1_999_598_125_000_000,
      attributes: [{ slug: "parasite", label: "Parasite", count: 1 }] },
  ].filter((category) => exactProfiles.some((profile) => profile.primarySlug.toLowerCase() === category.attributes[0]!.slug
    || profile.attributes.some((attribute) => attribute.slug.toLowerCase() === category.attributes[0]!.slug)));
  if (categories.length) {
    let total = 0;
    const ordered = [...output.sat_ranges].sort((a, b) => a[0] - b[0]);
    for (let index = 0; index < ordered.length; index += 1) {
      const [start, end] = ordered[index]!;
      if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || start < 0 || end <= start || end > SAT_SUPPLY
        || (index > 0 && start < ordered[index - 1]![1])) throw new Error("Complete non-overlapping sat ranges are required to verify this bundle");
      total += end - start;
    }
    if (total !== output.value) throw new Error("Complete sat ranges are required to verify this bundle");
  }
  const bySat = new Map(located.map((sat) => [sat.sat, sat]));
  let expanded = 0;
  for (const category of categories) {
    let rangeOffset = 0;
    for (const [start, end] of output.sat_ranges) {
      const from = Math.max(start, category.start);
      const to = Math.min(end, category.end);
      const count = Math.max(0, to - from);
      if (expanded + count > 5_000) break;
      for (let sat = from; sat < to; sat += 1) {
        const existing = bySat.get(sat);
        const attributes = new Map((existing?.attributes ?? []).map((attribute) => [attribute.slug, attribute]));
        for (const attribute of category.attributes) attributes.set(attribute.slug, attribute);
        bySat.set(sat, { sat, offset: rangeOffset + sat - start, rarity: existing?.rarity ?? "common",
          primarySlug: existing?.primarySlug ?? category.attributes[0]!.slug,
          primaryLabel: existing?.primaryLabel ?? category.attributes[0]!.label, attributes: [...attributes.values()] });
      }
      expanded += count;
      rangeOffset += end - start;
    }
  }
  return [...bySat.values()].sort((left, right) => left.offset - right.offset || left.sat - right.sat);
}

export function walletSatSourceIsUsable(source: Pick<WalletSatSource, "satOffset">): boolean {
  return (source.satOffset ?? 0) === 0;
}

function mergeAttributes(
  value: number,
  rodarmor: LocatedRodarmorSat[],
  profile?: SpecialSatProfile,
): SatAttributeCount[] {
  const counts = new Map<string, SatAttributeCount>();
  const nonCommonCount = rodarmor.length;
  counts.set("common", { slug: "common", label: "Common", count: Math.max(0, value - nonCommonCount) });
  for (const located of rodarmor) {
    const current = counts.get(located.rarity);
    counts.set(located.rarity, {
      slug: located.rarity,
      label: titleCase(located.rarity),
      count: (current?.count ?? 0) + 1,
    });
  }
  for (const attribute of profile?.attributes ?? []) {
    if (!attribute.slug || !attribute.label || !Number.isSafeInteger(attribute.count) || attribute.count <= 0) continue;
    const current = counts.get(attribute.slug);
    counts.set(attribute.slug, current ? { ...current, count: Math.max(current.count, attribute.count) } : attribute);
  }
  return [...counts.values()]
    .filter((attribute) => attribute.count > 0)
    .sort((left, right) => {
      const leftPriority = rarityPriority[left.slug as SatRarity] ?? -1;
      const rightPriority = rarityPriority[right.slug as SatRarity] ?? -1;
      return leftPriority - rightPriority || right.count - left.count || left.label.localeCompare(right.label);
    });
}

export function walletSatFromOutput(
  output: OrdinalOutputRecord,
  address: string,
  profile?: SpecialSatProfile,
): WalletSatSource | null {
  const firstSat = output.sat_ranges?.[0]?.[0];
  if (
    !Number.isSafeInteger(firstSat)
    || Number(firstSat) < 0
    || !Number.isSafeInteger(output.value)
    || output.value < 330
    || output.spent === true
    || (output.address && output.address !== address)
    || Boolean(output.inscriptions?.length)
    || Boolean(output.runes && Object.keys(output.runes).length)
  ) return null;

  const rodarmor = rodarmorSatsInRanges(output.sat_ranges);
  const profiledOffset = profile ? satOffsetInRanges(output.sat_ranges, profile.sat) : null;
  const selected = profiledOffset !== null
    ? { sat: profile?.sat ?? Number(firstSat), offset: profiledOffset }
    : rodarmor.sort((left, right) => rarityPriority[left.rarity] - rarityPriority[right.rarity] || left.offset - right.offset)[0]
      ?? { sat: Number(firstSat), offset: 0 };
  const rarity = satRarity(selected.sat);
  return {
    outpoint: output.outpoint,
    value: output.value,
    address,
    satRanges: output.sat_ranges,
    satOffset: selected.offset,
    sat: selected.sat,
    rarity,
    primarySlug: profiledOffset !== null ? profile?.primarySlug ?? rarity : rarity,
    primaryLabel: profiledOffset !== null ? profile?.primaryLabel ?? titleCase(rarity) : titleCase(rarity),
    attributes: mergeAttributes(output.value, rodarmor, profiledOffset !== null ? profile : undefined),
  };
}
